import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import useGetPageTitle from './useGetPageTitle';
import { useCreateOrderCart, useGetPartnerProducts } from '../../../components/Shared';
import { useGlobalState } from '../../../state/persistence';
import { PartnerProduct } from '../../../services';
import useStyles from './useStyles';
import { IntegratedPartnerTypeEnum, ReferenceLabelTypeEnum } from '../../../types';
import { PartnerProductAttribute } from '../../../services/PartnerService';
import { PartnerProductWithRefrenceInput } from '../PackageSelector';

const usePackage = () => {
  const { t } = useTranslation('package');
  const { dispatchProduct, dispatchQuestion, question, appStore } = useGlobalState();
  const products = useGetPartnerProducts();
  const { createOrderCart } = useCreateOrderCart();
  const [error, setError] = useState<string | undefined>();
  const [legalInsuranceConsent, setLegalInsuranceConsent] = useState(false);
  const [legalInsuranceConsentError, setLegalInsuranceConsentError] = useState(false);
  const [billingConsent, setBillingConsent] = useState<boolean>(false);
  const [billingConsentError, setBillingConsentError] = useState<boolean>(false);
  const [unionOptionsConsent, setUnionOptionsConsent] = useState<boolean>(false);
  const [unionOptionsConsentError, setUnionOptionsConsentError] = useState<boolean>(false);

  const onLegalInsuranceConsentChange = (_: unknown, checked: boolean) => setLegalInsuranceConsent(checked);
  const onBillingConsentChange = (_: unknown, checked: boolean) => setBillingConsent(checked);
  const onUnionOptionsChange = (_: unknown, checked: boolean) => setUnionOptionsConsent(checked);

  const partnerProduct = products?.find((p) => p.integratedPartner.type === IntegratedPartnerTypeEnum.LEGAL_INSURANCE);
  const showUnionOptions = question.hasUnionOptions && partnerProduct?.integratedPartner.options?.includes('union');

  let referenceLabel = partnerProduct?.integratedPartner?.referenceLabelContract;
  if (appStore.referenceLabelType === ReferenceLabelTypeEnum.CASE)
    referenceLabel = partnerProduct?.integratedPartner?.referenceLabelCase;
  if (
    appStore.referenceLabelType === ReferenceLabelTypeEnum.VOUCHER &&
    partnerProduct?.integratedPartner?.referenceLabelVoucher
  )
    referenceLabel = partnerProduct?.integratedPartner?.referenceLabelVoucher;

  const showReferenceInput = referenceLabel && referenceLabel.length > 0 && !showUnionOptions;

  const onReferenceChange = (event: any) => {
    dispatchQuestion('reference', event.target.value);
  };

  const onSubmit = (product: PartnerProductWithRefrenceInput) => {
    setError(undefined);
    setLegalInsuranceConsentError(false);
    let billingConsentTimestamp: string | undefined;
    let unionOptionsConsentTimestamp: string | undefined;

    const requiresUnionValidation = product?.integratedPartner.options?.includes('union') && showUnionOptions;
    if (!requiresUnionValidation && referenceLabel?.length && !question.reference && product.onReferenceChange) {
      setError(t('validation.reference.required', { referenceLabel }));
      return;
    }

    if (product?.requiresInsuranceProof && !legalInsuranceConsent) {
      setLegalInsuranceConsentError(true);
      return;
    }

    if (product?.requiresBillingConsent && !billingConsent) {
      setBillingConsentError(true);
      return;
    }

    if (requiresUnionValidation && !unionOptionsConsent) {
      setUnionOptionsConsentError(true);
      return;
    }

    if (product?.requiresBillingConsent) billingConsentTimestamp = new Date().toISOString();
    if (requiresUnionValidation) unionOptionsConsentTimestamp = new Date().toISOString();
    return createOrderCart(billingConsentTimestamp, unionOptionsConsentTimestamp).catch((error: Error) => {
      setError(error.message);
      if (error.cause !== 'INVALID_REFERENCE') console.error(error);
    });
  };

  const commonProductAttributes =
    products?.reduce((acc: PartnerProductAttribute[], product: PartnerProduct) => {
      const productAttributes = product.attributesIncluded;
      if (acc.length === 0) {
        return productAttributes;
      }
      return acc.filter((attr) => productAttributes.some((pAttr) => pAttr.id === attr.id));
    }, []) || [];

  const commonProductAttributesIds = commonProductAttributes.map((a) => a.id);

  const partnerProductWithRefrenceInput = {
    ...partnerProduct,
    ...(showReferenceInput && { referenceLabel, onReferenceChange, error, reference: question.reference }),
    legalInsuranceConsent,
    legalInsuranceConsentError,
    onLegalInsuranceConsentChange,
    billingConsent,
    billingConsentError,
    onBillingConsentChange,
    unionOptionsConsent,
    unionOptionsConsentError,
    onUnionOptionsChange,
    requiresUnionOptionsConsent: showUnionOptions,
    attributesIncluded: partnerProduct?.attributesIncluded.filter((a) => !commonProductAttributesIds.includes(a.id)),
  };

  let productsWithReferenceInput = products?.map((product) => {
    if (product.id === partnerProductWithRefrenceInput.id) return partnerProductWithRefrenceInput;
    return {
      ...product,
      highlighted: !partnerProductWithRefrenceInput?.id && product.highlighted,
      attributesIncluded: product?.attributesIncluded.filter((a) => !commonProductAttributesIds.includes(a.id)),
      logoCompensation:
        partnerProductWithRefrenceInput?.id &&
        partnerProductWithRefrenceInput?.integratedPartner?.partnerSettings.productLogo,
    };
  });

  if (partnerProductWithRefrenceInput?.id) {
    productsWithReferenceInput = productsWithReferenceInput?.sort((a, b) => (a.price || 0) - (b.price || 0));
  }

  const onSelect = useCallback(
    (id: string) => {
      setError(undefined);
      if (!products) return;

      const product = productsWithReferenceInput?.find((p) => p.id === id);
      dispatchProduct('id', id);
      dispatchProduct('price', product?.price || 0);
      dispatchProduct('name', product?.name);

      if (product?.integratedPartner?.id && appStore.partner && !product?.onReferenceChange) {
        if (product?.integratedPartner?.id !== appStore.partner?.id) {
          dispatchQuestion('reference', undefined);
        } else {
          dispatchQuestion('reference', appStore.partnerReference);
        }
      }

      onSubmit(product);
    },
    [appStore.referenceLabelType, dispatchProduct, products, onSubmit],
  );

  return {
    t,
    pageTitle: useGetPageTitle(),
    classes: useStyles(),
    products: productsWithReferenceInput,
    onSelect,
    error,
    onSubmit,
    reference: question.reference,
    showUnionOptions,
    commonProductAttributes,
    partnerProductWithRefrenceInput,
  };
};

export default usePackage;
